// src/components/businesses/BusinessAdmin.tsx
// Top-level "Negocios" UI: list + create + detail (tabs: General, Meta, Contenido).
// Each Business is a brand/cuenta independiente dentro de la Org.

import { Component, type ErrorInfo, type ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Activity, AlertTriangle, ArrowLeft, Building2, Calendar as CalendarIcon,
  CheckCircle, ChevronRight, Facebook, Image as ImageIcon, Instagram,
  Link as LinkIcon, Loader2, LogOut, Plus, Save, Settings, Trash2, Upload,
} from 'lucide-react';

import {
  ApiError,
  connectMetaAccounts, createBusiness, createCatalogItem, deleteBusiness,
  deleteCatalogItem, disconnectMetaAccounts, getAvailableMetaAccounts,
  getBusiness, getBusinessEvents, getContentConfig, listBusinesses,
  listCatalog, updateBusiness, updateContentConfig, uploadBusinessLogo,
  type AvailableMetaAccounts, type Business, type BusinessCatalogItem,
  type BusinessEvent, type BusinessIn, type ContentConfig, type ContentConfigIn,
} from '@/lib/api_businesses';
import { uploadMedia } from '@/lib/api_social_publish';

type NotifyKind = 'success' | 'error' | 'info';
type AddNotification = (n: { type: NotifyKind; title: string; message?: string }) => void;
type Notifier = (t: NotifyKind, msg: string) => void;

interface Props { addNotification?: AddNotification; }

class BusinessErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null; info: ErrorInfo | null }
> {
  state = { error: null as Error | null, info: null as ErrorInfo | null };
  static getDerivedStateFromError(error: Error) { return { error, info: null }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('BusinessAdmin crash:', error, info);
    this.setState({ error, info });
  }
  render() {
    if (this.state.error) {
      return (
        <div className="p-6 max-w-3xl">
          <div className="o-card border-red-500/40 bg-red-500/5 space-y-3">
            <h2 className="text-red-300 font-bold">⚠ Error de render en Negocios</h2>
            <pre className="text-xs text-white/70 whitespace-pre-wrap break-all">{this.state.error?.message}</pre>
            <pre className="text-[10px] text-white/40 whitespace-pre-wrap break-all">{this.state.error?.stack}</pre>
            {this.state.info?.componentStack && (
              <pre className="text-[10px] text-white/40 whitespace-pre-wrap break-all">{this.state.info.componentStack}</pre>
            )}
            <button onClick={() => this.setState({ error: null, info: null })} className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white/80 text-xs">Reintentar</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function BusinessAdmin(props: Props) {
  return <BusinessErrorBoundary><BusinessAdminInner {...props} /></BusinessErrorBoundary>;
}

function BusinessAdminInner({ addNotification }: Props) {
  const notify: Notifier = (t, msg) =>
    addNotification?.({ type: t, title: msg });

  const [rows, setRows] = useState<Business[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listBusinesses(true));
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return (
      <BusinessDetail
        businessId={selectedId}
        onBack={() => { setSelectedId(null); load(); }}
        notify={notify}
      />
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 size={24} style={{ color: 'var(--acc1)' }} /> Negocios
          </h1>
          <p className="text-white/50 text-sm mt-1">
            Cada negocio es una marca con sus propias cuentas Meta (Facebook, Instagram, Ads) y configuración de contenido.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="px-4 py-2 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 text-[var(--acc1)] flex items-center gap-2"
        >
          <Plus size={14} /> Nuevo negocio
        </button>
      </div>

      {creating && (
        <CreateBusinessCard
          onCancel={() => setCreating(false)}
          onCreated={(b) => { setCreating(false); setSelectedId(b.id); load(); notify('success', `Negocio "${b.name}" creado`); }}
          notify={notify}
        />
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-8 justify-center text-white/40 text-sm">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      ) : !rows || rows.length === 0 ? (
        <div className="o-card text-center py-12 text-white/50">
          <Building2 size={32} className="mx-auto mb-3 text-white/20" />
          <p>Todavía no tienes negocios.</p>
          <p className="text-xs mt-1">Crea uno para poder publicar, configurar auto-posting y administrar sus cuentas Meta.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((b) => <BusinessCard key={b.id} b={b} onClick={() => setSelectedId(b.id)} />)}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// List card
// ══════════════════════════════════════════════════════════════

function BusinessCard({ b, onClick }: { b: Business; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`o-card text-left space-y-3 hover:border-[var(--acc1)]/40 transition-colors ${b.is_active ? '' : 'opacity-60'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            {b.logo_url
              ? <img src={b.logo_url} alt="" className="w-6 h-6 rounded object-cover" />
              : <Building2 size={18} className="text-white/40" />}
            <h3 className="text-white font-semibold">{b.name}</h3>
          </div>
          <div className="text-[10px] text-white/30 font-mono mt-0.5">{b.slug}</div>
        </div>
        <ChevronRight size={16} className="text-white/30 shrink-0 mt-1" />
      </div>

      <div className="flex flex-wrap gap-1.5 text-[10px]">
        <Badge ok={b.has_meta_connected} icon={<Facebook size={10} />} label={b.fb_page_name || 'Sin FB'} />
        <Badge ok={b.has_instagram} icon={<Instagram size={10} />} label={b.ig_username || 'Sin IG'} />
        <Badge ok={b.has_ads} icon={<LinkIcon size={10} />} label={b.ad_account_name || 'Sin Ads'} />
      </div>
      {!b.is_active && (
        <div className="text-[10px] text-amber-400/70">Inactivo</div>
      )}
    </button>
  );
}

function Badge({ ok, icon, label }: { ok: boolean; icon: React.ReactNode; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-white/70 ${ok ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-white/10 bg-white/5 text-white/40'}`}>
      {icon} <span className="truncate max-w-[10em]">{label}</span>
    </span>
  );
}

// ══════════════════════════════════════════════════════════════
// Create form
// ══════════════════════════════════════════════════════════════

function CreateBusinessCard({
  onCancel, onCreated, notify,
}: {
  onCancel: () => void;
  onCreated: (b: Business) => void;
  notify: Notifier;
}) {
  const [data, setData] = useState<BusinessIn>({ name: '', description: '', website: '' });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!data.name.trim()) return notify('error', 'El nombre es requerido');
    setBusy(true);
    try {
      const b = await createBusiness(data);
      onCreated(b);
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally { setBusy(false); }
  };

  return (
    <div className="o-card space-y-3">
      <div className="flex items-center gap-2">
        <Plus size={18} style={{ color: 'var(--acc1)' }} />
        <h2 className="text-lg font-bold text-white">Nuevo negocio</h2>
      </div>
      <Field label="Nombre *">
        <input className="input-base" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="Agathos Creative House" />
      </Field>
      <Field label="Descripción">
        <textarea className="input-base h-20" value={data.description || ''} onChange={(e) => setData({ ...data, description: e.target.value })} placeholder="A qué se dedica este negocio" />
      </Field>
      <Field label="Sitio web">
        <input className="input-base" value={data.website || ''} onChange={(e) => setData({ ...data, website: e.target.value })} placeholder="https://..." />
      </Field>
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 text-[var(--acc1)] flex items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Crear
        </button>
        <button onClick={onCancel} disabled={busy} className="px-3 py-2 rounded-lg text-white/50 hover:text-white/80">Cancelar</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Detail view
// ══════════════════════════════════════════════════════════════

type DetailTab = 'general' | 'meta' | 'content' | 'catalog' | 'activity';

function BusinessDetail({
  businessId, onBack, notify,
}: {
  businessId: string;
  onBack: () => void;
  notify: Notifier;
}) {
  const [tab, setTab] = useState<DetailTab>('general');
  const [biz, setBiz] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBiz(await getBusiness(businessId));
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
      onBack();
    } finally { setLoading(false); }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !biz) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-white/40 text-sm">
        <Loader2 size={14} className="animate-spin" /> Cargando…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      <button onClick={onBack} className="text-white/50 hover:text-white text-sm flex items-center gap-1">
        <ArrowLeft size={14} /> Negocios
      </button>

      <div className="flex items-start gap-3">
        {biz.logo_url
          ? <img src={biz.logo_url} alt="" className="w-12 h-12 rounded object-cover" />
          : <Building2 size={32} className="text-white/30" />}
        <div>
          <h1 className="text-2xl font-bold text-white">{biz.name}</h1>
          <div className="text-xs text-white/40 font-mono mt-0.5">{biz.slug} · {biz.id.slice(0, 8)}</div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-white/10">
        <TabBtn active={tab === 'general'} onClick={() => setTab('general')} icon={<Settings size={14} />} label="General" />
        <TabBtn active={tab === 'meta'} onClick={() => setTab('meta')} icon={<Facebook size={14} />} label="Cuentas Meta" />
        <TabBtn active={tab === 'content'} onClick={() => setTab('content')} icon={<Instagram size={14} />} label="Config de contenido" />
        <TabBtn active={tab === 'catalog'} onClick={() => setTab('catalog')} icon={<ImageIcon size={14} />} label="Catálogo" />
        <TabBtn active={tab === 'activity'} onClick={() => setTab('activity')} icon={<Activity size={14} />} label="Actividad" />
      </div>

      {tab === 'general' && <GeneralTab biz={biz} onSaved={load} notify={notify} />}
      {tab === 'meta' && <MetaTab biz={biz} onChanged={load} notify={notify} />}
      {tab === 'content' && <ContentTab businessId={biz.id} notify={notify} />}
      {tab === 'catalog' && <CatalogTab businessId={biz.id} notify={notify} />}
      {tab === 'activity' && <ActivityTab businessId={biz.id} notify={notify} />}
    </div>
  );
}

function TabBtn({ active, onClick, icon, label }: any) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 transition-colors ${active ? 'border-[var(--acc1)] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
    >
      {icon} {label}
    </button>
  );
}

// ── General tab ───────────────────────────────────────────────

function GeneralTab({ biz, onSaved, notify }: { biz: Business; onSaved: () => void; notify: Notifier }) {
  const [data, setData] = useState({
    name: biz.name, description: biz.description, website: biz.website, logo_url: biz.logo_url,
    primary_color: biz.primary_color, secondary_color: biz.secondary_color, is_active: biz.is_active,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await updateBusiness(biz.id, data);
      notify('success', 'Cambios guardados');
      onSaved();
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!confirm(`¿Eliminar "${biz.name}"? Se archiva (soft-delete), puedes restaurarlo después.`)) return;
    setBusy(true);
    try {
      await deleteBusiness(biz.id);
      notify('success', 'Negocio eliminado');
      onSaved();
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="o-card space-y-3">
        <Field label="Nombre"><input className="input-base" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} /></Field>
        <Field label="Descripción"><textarea className="input-base h-20" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} /></Field>
        <Field label="Sitio web"><input className="input-base" value={data.website} onChange={(e) => setData({ ...data, website: e.target.value })} /></Field>
        <Field label="Logo">
          <div className="flex items-center gap-3">
            {data.logo_url
              ? <img src={data.logo_url} alt="" className="w-12 h-12 rounded object-cover border border-white/10" />
              : <div className="w-12 h-12 rounded bg-white/5 border border-white/10 flex items-center justify-center text-[10px] text-white/40">sin logo</div>}
            <label className="px-3 py-2 text-xs rounded bg-white/5 hover:bg-white/10 border border-white/10 cursor-pointer flex items-center gap-2">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
              {data.logo_url ? 'Cambiar' : 'Subir'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                disabled={busy}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setBusy(true);
                  try {
                    const updated = await uploadBusinessLogo(biz.id, file);
                    setData(d => ({ ...d, logo_url: updated.logo_url }));
                    notify('success', 'Logo actualizado');
                    onSaved();
                  } catch (err: any) {
                    notify('error', err instanceof ApiError ? err.message : err?.message || 'Error');
                  } finally {
                    setBusy(false);
                    e.target.value = '';
                  }
                }}
              />
            </label>
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Color primario"><input className="input-base" value={data.primary_color} onChange={(e) => setData({ ...data, primary_color: e.target.value })} placeholder="#0ea5e9" /></Field>
          <Field label="Color secundario"><input className="input-base" value={data.secondary_color} onChange={(e) => setData({ ...data, secondary_color: e.target.value })} placeholder="#f59e0b" /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-white/70 pt-1">
          <input type="checkbox" checked={data.is_active} onChange={(e) => setData({ ...data, is_active: e.target.checked })} />
          Activo
        </label>
        <div className="flex justify-between pt-2">
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 text-[var(--acc1)] flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
          </button>
          <button onClick={doDelete} disabled={busy} className="px-3 py-2 text-[11px] text-red-400 hover:bg-red-500/10 rounded flex items-center gap-1">
            <Trash2 size={12} /> Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Meta accounts tab ─────────────────────────────────────────

function MetaTab({ biz, onChanged, notify }: { biz: Business; onChanged: () => void; notify: Notifier }) {
  const [pool, setPool] = useState<AvailableMetaAccounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'page' | 'ad' | 'disconnect' | null>(null);

  useEffect(() => {
    getAvailableMetaAccounts(biz.id).then(setPool).catch((e) => notify('error', e.message)).finally(() => setLoading(false));
  }, [biz.id]);

  const pickPage = async (pageId: string) => {
    setBusy('page');
    try {
      await connectMetaAccounts(biz.id, { fb_page_id: pageId, ad_account_id: biz.ad_account_id || null });
      notify('success', 'Página conectada al negocio');
      onChanged();
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally { setBusy(null); }
  };

  const pickAd = async (adId: string) => {
    if (!biz.fb_page_id) return notify('error', 'Primero selecciona la página de Facebook');
    setBusy('ad');
    try {
      await connectMetaAccounts(biz.id, { fb_page_id: biz.fb_page_id, ad_account_id: adId });
      notify('success', 'Cuenta publicitaria conectada');
      onChanged();
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally { setBusy(null); }
  };

  const disconnect = async () => {
    if (!confirm('¿Desconectar cuentas Meta de este negocio?')) return;
    setBusy('disconnect');
    try {
      await disconnectMetaAccounts(biz.id);
      notify('success', 'Cuentas desconectadas');
      onChanged();
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally { setBusy(null); }
  };

  if (loading) return <div className="py-8 text-white/40 text-sm flex items-center gap-2 justify-center"><Loader2 size={14} className="animate-spin" /> Cargando pool OAuth…</div>;

  if (!pool?.connected) {
    return (
      <div className="o-card max-w-xl">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={16} className="text-amber-400" />
          <h3 className="text-white font-semibold">Meta no conectado a nivel Org</h3>
        </div>
        <p className="text-white/60 text-sm">
          Antes de asignar páginas a un negocio, la Org debe conectarse a Meta.
          Ve a <strong>Empresa → Meta</strong> y haz el OAuth primero.
        </p>
      </div>
    );
  }

  const notConnected = !biz.has_meta_connected;

  return (
    <div className="max-w-2xl space-y-4">
      <div className="o-card space-y-2">
        <h3 className="text-white text-sm font-semibold">Estado actual</h3>
        {notConnected ? (
          <div className="flex items-center gap-2 text-amber-300/80 text-sm">
            <AlertTriangle size={14} />
            No conectado a Meta. Selecciona una página abajo para conectar.
          </div>
        ) : (
          <>
            <Row label="Página FB" value={biz.fb_page_name || '(ninguna)'} />
            <Row label="Instagram" value={biz.ig_username ? `@${biz.ig_username}` : '(ninguno)'} />
            <Row label="Ad account" value={biz.ad_account_name || biz.ad_account_id || '(ninguna)'} />
            <button onClick={disconnect} disabled={busy === 'disconnect'} className="text-[11px] text-red-400 hover:bg-red-500/10 flex items-center gap-1 px-2 py-1 rounded mt-2 w-fit">
              {busy === 'disconnect' ? <Loader2 size={11} className="animate-spin" /> : <LogOut size={11} />} Desconectar
            </button>
          </>
        )}
      </div>

      <div className="o-card space-y-2">
        <h3 className="text-white text-sm font-semibold">
          {notConnected ? 'Conectar Meta — elige una página' : 'Cambiar página de Facebook'}
        </h3>
        <p className="text-white/50 text-xs">
          {notConnected
            ? 'Al hacer click en una página, se conecta con su IG Business (si tiene) y su token permanente.'
            : 'Click en otra página para cambiar — la conexión anterior se sobreescribe.'}
        </p>
        <div className="space-y-1 mt-2">
          {pool.pages.map((p) => {
            const active = p.id === biz.fb_page_id;
            return (
              <button key={p.id} onClick={() => pickPage(p.id)} disabled={!!busy} className={`w-full text-left px-3 py-2 rounded-lg border flex items-center justify-between gap-2 ${active ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                <div>
                  <div className="text-white text-sm">{p.name}</div>
                  <div className="text-[10px] text-white/40 font-mono">{p.id}{p.category ? ` · ${p.category}` : ''}</div>
                </div>
                {active ? <CheckCircle size={14} className="text-emerald-400 shrink-0" /> : <ChevronRight size={14} className="text-white/30 shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>

      {pool.ad_accounts.length > 0 && (
        <div className="o-card space-y-2">
          <h3 className="text-white text-sm font-semibold">Cuenta publicitaria</h3>
          <div className="space-y-1 mt-2">
            {pool.ad_accounts.map((a) => {
              const active = a.id === biz.ad_account_id;
              return (
                <button key={a.id} onClick={() => pickAd(a.id)} disabled={!!busy || !biz.fb_page_id} className={`w-full text-left px-3 py-2 rounded-lg border flex items-center justify-between gap-2 ${active ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                  <div>
                    <div className="text-white text-sm">{a.name || a.id}</div>
                    <div className="text-[10px] text-white/40 font-mono">{a.id}{a.business_name ? ` · ${a.business_name}` : ''}{a.currency ? ` · ${a.currency}` : ''}</div>
                  </div>
                  {active ? <CheckCircle size={14} className="text-emerald-400 shrink-0" /> : <ChevronRight size={14} className="text-white/30 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Content config tab ────────────────────────────────────────

function ContentTab({ businessId, notify }: { businessId: string; notify: Notifier }) {
  const [cfg, setCfg] = useState<ContentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<ContentConfigIn>({});
  const [topicsText, setTopicsText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await getContentConfig(businessId);
      setCfg(c);
      setForm({});
      setTopicsText((c.content_topics || []).join('\n'));
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally { setLoading(false); }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const current = { ...cfg, ...form } as ContentConfig;

  const patch = (k: keyof ContentConfigIn, v: any) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      const topics = topicsText.split('\n').map((s) => s.trim()).filter(Boolean);
      const out = await updateContentConfig(businessId, { ...form, content_topics: topics });
      setCfg(out);
      setForm({});
      notify('success', `Configuración guardada (v${out.version})`);
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally { setBusy(false); }
  };

  if (loading || !cfg) return <div className="py-8 text-white/40 text-sm flex items-center gap-2 justify-center"><Loader2 size={14} className="animate-spin" /> Cargando…</div>;

  return (
    <div className="max-w-3xl space-y-4">
      <div className="o-card space-y-3">
        <h3 className="text-white font-semibold">Activación</h3>
        <Toggle label="Auto-generar contenido (prende el dispatcher)" value={current.is_enabled} onChange={(v) => patch('is_enabled', v)} />
        <Toggle label="Auto-publicar (sin aprobación manual)" value={current.auto_publish} onChange={(v) => patch('auto_publish', v)} />
        <Toggle label="Publicar inmediatamente (ignora peak hours)" value={current.publish_immediately} onChange={(v) => patch('publish_immediately', v)} />
      </div>

      <div className="o-card space-y-3">
        <h3 className="text-white font-semibold">Cadencia</h3>
        <Field label="Frecuencia">
          <select className="input-base" value={current.frequency_type} onChange={(e) => patch('frequency_type', e.target.value)}>
            <option value="posts_per_week">Posts por semana</option>
            <option value="interval_days">Cada N días</option>
          </select>
        </Field>
        {current.frequency_type === 'posts_per_week' ? (
          <Field label="Posts por semana">
            <input type="number" min={1} max={30} className="input-base" value={current.posts_per_week} onChange={(e) => patch('posts_per_week', Number(e.target.value))} />
          </Field>
        ) : (
          <Field label="Intervalo (días)">
            <input type="number" min={1} max={60} className="input-base" value={current.interval_days} onChange={(e) => patch('interval_days', Number(e.target.value))} />
          </Field>
        )}
      </div>

      <div className="o-card space-y-3">
        <h3 className="text-white font-semibold">Identidad de marca</h3>
        <Field label="Descripción de la marca"><textarea className="input-base h-20" value={current.brand_description} onChange={(e) => patch('brand_description', e.target.value)} /></Field>
        <Field label="Productos / Servicios"><textarea className="input-base h-20" value={current.products_services} onChange={(e) => patch('products_services', e.target.value)} /></Field>
        <Field label="Público objetivo"><textarea className="input-base h-16" value={current.target_audience} onChange={(e) => patch('target_audience', e.target.value)} /></Field>
        <Field label="Tono de voz"><input className="input-base" value={current.tone_of_voice} onChange={(e) => patch('tone_of_voice', e.target.value)} placeholder="Cálido y profesional" /></Field>
        <Field label="Estilo visual"><input className="input-base" value={current.visual_style} onChange={(e) => patch('visual_style', e.target.value)} placeholder="Moderno minimalista con acentos neón" /></Field>
        <Field label="Notas al LLM (se interpolan siempre)"><textarea className="input-base h-20" value={current.generation_notes} onChange={(e) => patch('generation_notes', e.target.value)} /></Field>
      </div>

      <div className="o-card space-y-3">
        <h3 className="text-white font-semibold">Estrategia</h3>
        <Field label="Pilares / temas (uno por línea)">
          <textarea className="input-base h-32" value={topicsText} onChange={(e) => setTopicsText(e.target.value)} placeholder={'Cultura local\nBehind the scenes\nTestimonios\n…'} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo de cliente">
            <select className="input-base" value={current.client_type} onChange={(e) => patch('client_type', e.target.value)}>
              <option value="services">Servicios</option>
              <option value="products">Productos</option>
              <option value="personal">Personal</option>
            </select>
          </Field>
          <Field label="Idioma">
            <select className="input-base" value={current.language} onChange={(e) => patch('language', e.target.value)}>
              <option value="es">Español</option>
              <option value="en">English</option>
              <option value="pt">Português</option>
              <option value="fr">Français</option>
            </select>
          </Field>
        </div>
        <Field label="Modo de imagen">
          <select className="input-base" value={current.image_mode} onChange={(e) => patch('image_mode', e.target.value)}>
            <option value="ai_generated">IA genera desde cero</option>
            <option value="ai_enhanced">IA sobre foto del catálogo</option>
          </select>
          <p className="text-[10px] text-white/40 mt-1">
            Si el negocio tiene logo (<em>General → Logo URL</em>), se pasa como imagen de referencia al generador (gpt-image-2) para que lo integre en la esquina inferior derecha respetando colores y forma exactos.
          </p>
        </Field>
      </div>

      <div className="o-card space-y-3">
        <h3 className="text-white font-semibold">Plataformas</h3>
        <Toggle label="Facebook" value={current.post_to_facebook} onChange={(v) => patch('post_to_facebook', v)} />
        <Toggle label="Instagram" value={current.post_to_instagram} onChange={(v) => patch('post_to_instagram', v)} />
      </div>

      <div className="o-card space-y-3">
        <h3 className="text-white font-semibold">Stories y Reels animados</h3>
        <p className="text-white/50 text-xs">
          Genera Stories y Reels con plantillas de video animadas (Remotion).
          Cada pieza incluye narración de voz (ElevenLabs con fallback a Microsoft TTS gratis).
        </p>

        <Toggle
          label="Generar Stories automáticamente"
          value={!!current.video_enabled}
          onChange={(v) => patch('video_enabled', v)}
        />

        {current.video_enabled && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Stories por semana">
                <input type="number" min={0} max={14} className="input-base" value={current.stories_per_week ?? 0} onChange={(e) => patch('stories_per_week', Number(e.target.value))} placeholder="3" />
              </Field>
              <Field label="Reels por semana">
                <input type="number" min={0} max={7} className="input-base" value={current.reels_per_week ?? 0} onChange={(e) => patch('reels_per_week', Number(e.target.value))} placeholder="1" />
              </Field>
            </div>
            <Field label="Calidad (tier)">
              <select className="input-base" value={current.video_tier || 'basico'} onChange={(e) => patch('video_tier', e.target.value)}>
                <option value="basico">Básico — Remotion (gratis, rápido)</option>
                <option value="pro">Pro — Kling 2.6 ($0.07/s)</option>
                <option value="premium">Premium — Kling 3.0 ($0.17/s)</option>
                <option value="ultra">Ultra — Seedance 2.0 ($0.30/s)</option>
              </select>
              <p className="text-[10px] text-white/40 mt-1">
                Fallback automático: si un tier alto falla o rechaza la imagen, cae al siguiente hacia abajo hasta Remotion.
              </p>
            </Field>
            <p className="text-[10px] text-white/40">
              Stories y Reels se generan independientemente según los counts arriba. 0 = no generar ese tipo. "Generar ahora" (en Aprobar drafts) dispara también los que tengan count &gt; 0.
            </p>
          </>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[11px] text-white/40">Versión actual: v{cfg.version} · Última edición {new Date(cfg.updated_at).toLocaleString('es-MX')}</div>
        <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 text-[var(--acc1)] flex items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar configuración
        </button>
      </div>

      <VersionHistory businessId={businessId} currentSnapshot={_serializeForDiff(current)} />
    </div>
  );
}

function _serializeForDiff(c: ContentConfig): Record<string, unknown> {
  return {
    is_enabled: c.is_enabled,
    auto_publish: c.auto_publish,
    publish_immediately: c.publish_immediately,
    frequency_type: c.frequency_type,
    posts_per_week: c.posts_per_week,
    interval_days: c.interval_days,
    brand_description: c.brand_description,
    products_services: c.products_services,
    target_audience: c.target_audience,
    tone_of_voice: c.tone_of_voice,
    visual_style: c.visual_style,
    generation_notes: c.generation_notes,
    content_topics: c.content_topics,
    client_type: c.client_type,
    image_mode: c.image_mode,
    language: c.language,
    post_to_facebook: c.post_to_facebook,
    post_to_instagram: c.post_to_instagram,
  };
}

// ── Version history + diff viewer ─────────────────────────────

function VersionHistory({
  businessId, currentSnapshot,
}: {
  businessId: string;
  currentSnapshot: Record<string, unknown>;
}) {
  const [versions, setVersions] = useState<import('@/lib/api_businesses').ContentConfigVersion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<import('@/lib/api_businesses').ContentConfigVersion | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await (await import('@/lib/api_businesses')).listContentConfigVersions(businessId, 30);
      setVersions(rows);
    } catch {
      setVersions([]);
    } finally { setLoading(false); }
  }, [businessId]);

  useEffect(() => { if (open && !versions) load(); }, [open, versions, load]);

  return (
    <div className="o-card space-y-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
      >
        <div>
          <h3 className="text-white text-sm font-semibold">Historial de versiones</h3>
          <p className="text-white/40 text-[11px]">
            Diff entre la config actual y versiones anteriores.
          </p>
        </div>
        <ChevronRight size={14} className={`text-white/40 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="space-y-2 pt-2 border-t border-white/5">
          {loading ? (
            <div className="py-3 flex justify-center text-white/40 text-xs"><Loader2 size={12} className="animate-spin" /></div>
          ) : !versions || versions.length === 0 ? (
            <div className="text-white/40 text-xs py-2">Aún no hay versiones guardadas.</div>
          ) : (
            <>
              <div className="flex flex-wrap gap-1">
                {versions.map((v) => {
                  const active = picked?.id === v.id;
                  return (
                    <button
                      key={v.id}
                      onClick={() => setPicked(active ? null : v)}
                      className={`px-2 py-1 rounded text-[11px] border ${active
                        ? 'bg-[var(--acc1)]/20 border-[var(--acc1)]/40 text-[var(--acc1)]'
                        : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}
                    >
                      v{v.version} · {new Date(v.created_at).toLocaleDateString('es-MX')}
                    </button>
                  );
                })}
              </div>

              {picked && (
                <div className="mt-2 space-y-1.5">
                  <div className="text-[11px] text-white/50">
                    Diff: v{picked.version} ({new Date(picked.created_at).toLocaleString('es-MX')}) vs actual
                  </div>
                  <DiffView older={picked.data} newer={currentSnapshot} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DiffView({ older, newer }: {
  older: Record<string, unknown>;
  newer: Record<string, unknown>;
}) {
  const keys = Array.from(new Set([...Object.keys(older), ...Object.keys(newer)])).sort();
  const diff = keys
    .map((k) => ({ k, o: older[k], n: newer[k] }))
    .filter((d) => JSON.stringify(d.o) !== JSON.stringify(d.n));

  if (diff.length === 0) {
    return <div className="text-[11px] text-emerald-400/70">Sin cambios vs la versión actual.</div>;
  }

  return (
    <div className="space-y-2">
      {diff.map((d) => (
        <div key={d.k} className="text-[11px] border border-white/10 rounded p-2 bg-black/30">
          <div className="text-white/70 font-mono text-[10px] mb-1">{d.k}</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[9px] uppercase text-red-400/70">Anterior</div>
              <pre className="text-white/60 whitespace-pre-wrap break-all max-h-32 overflow-auto">{fmt(d.o)}</pre>
            </div>
            <div>
              <div className="text-[9px] uppercase text-emerald-400/70">Actual</div>
              <pre className="text-white/80 whitespace-pre-wrap break-all max-h-32 overflow-auto">{fmt(d.n)}</pre>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function fmt(v: unknown): string {
  if (v === undefined || v === null) return '(vacío)';
  if (typeof v === 'string') return v || '(vacío)';
  return JSON.stringify(v, null, 2);
}

// ── Catalog tab ───────────────────────────────────────────────

function CatalogTab({ businessId, notify }: { businessId: string; notify: Notifier }) {
  const [rows, setRows] = useState<BusinessCatalogItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState('');
  const [dow, setDow] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listCatalog(businessId));
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally { setLoading(false); }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!name.trim()) return notify('error', 'Pon un nombre al item');
    if (!file) return notify('error', 'Sube una imagen');
    setUploading(true);
    try {
      const up = await uploadMedia(file);
      await createCatalogItem(businessId, {
        name: name.trim(), image_url: up.media_url, day_of_week: dow.trim(),
      });
      notify('success', `Agregado: ${name}`);
      setName(''); setDow(''); setFile(null);
      await load();
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally { setUploading(false); }
  };

  const remove = async (id: string, itemName: string) => {
    if (!confirm(`¿Eliminar "${itemName}" del catálogo?`)) return;
    try {
      await deleteCatalogItem(businessId, id);
      notify('success', 'Eliminado');
      await load();
    } catch (e: any) {
      notify('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <div className="o-card space-y-3">
        <div className="flex items-center gap-2">
          <Upload size={16} style={{ color: 'var(--acc1)' }} />
          <h3 className="text-white font-semibold">Agregar al catálogo</h3>
        </div>
        <p className="text-white/50 text-xs">
          Fotos de productos / escenarios que el generador puede usar como referencia
          (en modo <em>IA sobre foto</em>) o publicar tal cual (modo <em>Foto del cliente</em>).
          La rotación es <strong>menos usadas primero</strong> — opcional: restringe a ciertos días.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nombre">
            <input className="input-base" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Plato estrella del menú" />
          </Field>
          <Field label="Días de uso (opcional)">
            <input className="input-base" value={dow} onChange={(e) => setDow(e.target.value)} placeholder="0,4 = Lun + Vie. Vacío = cualquier día." />
          </Field>
        </div>
        <Field label="Archivo">
          <input type="file" accept="image/*" className="input-base" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </Field>
        <button
          onClick={submit}
          disabled={uploading}
          className="px-4 py-2 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 text-[var(--acc1)] flex items-center gap-2"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Agregar item
        </button>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center text-white/40 text-sm"><Loader2 size={14} className="animate-spin" /></div>
      ) : !rows || rows.length === 0 ? (
        <div className="o-card text-center py-8 text-white/50 text-sm">
          <ImageIcon size={24} className="mx-auto mb-2 text-white/20" />
          Aún no hay items. Sube el primero arriba.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {rows.map((it) => (
            <div key={it.id} className="o-card space-y-2">
              {it.image_url
                ? <img src={it.image_url} alt={it.name} className="w-full h-32 object-cover rounded" />
                : <div className="w-full h-32 bg-white/5 rounded flex items-center justify-center"><ImageIcon size={20} className="text-white/30" /></div>}
              <div>
                <div className="text-white text-sm truncate">{it.name}</div>
                <div className="flex gap-2 text-[10px] text-white/40 mt-0.5">
                  <span>Usado {it.times_used}x</span>
                  {it.day_of_week && <span>· Días: {it.day_of_week}</span>}
                </div>
              </div>
              <button
                onClick={() => remove(it.id, it.name)}
                className="text-[11px] text-red-400 hover:bg-red-500/10 rounded px-2 py-1 flex items-center gap-1 w-fit"
              >
                <Trash2 size={11} /> Eliminar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Activity tab (event timeline) ─────────────────────────────

function ActivityTab({ businessId, notify }: { businessId: string; notify: Notifier }) {
  const [rows, setRows] = useState<BusinessEvent[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBusinessEvents(businessId, 100)
      .then(setRows)
      .catch((e) => notify('error', e instanceof ApiError ? e.message : e?.message || 'Error'))
      .finally(() => setLoading(false));
  }, [businessId]);

  if (loading) {
    return <div className="py-8 flex justify-center text-white/40 text-sm"><Loader2 size={14} className="animate-spin" /></div>;
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="o-card max-w-2xl text-center py-8 text-white/50 text-sm">
        <Activity size={24} className="mx-auto mb-2 text-white/20" />
        Sin actividad aún.
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-2">
      {rows.map((e) => (
        <div key={e.id} className="o-card flex items-start gap-3">
          <div className="mt-0.5 text-[var(--acc1)]"><Activity size={14} /></div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm">
              {labelForKind(e.kind)}
              {e.actor && <span className="text-[11px] text-white/40 ml-2">· {e.actor}</span>}
            </div>
            <div className="text-[10px] text-white/40 mt-0.5 flex items-center gap-1">
              <CalendarIcon size={10} /> {new Date(e.created_at).toLocaleString('es-MX')}
            </div>
            {e.payload && Object.keys(e.payload).length > 0 && (
              <pre className="mt-1 text-[10px] text-white/50 bg-black/30 p-2 rounded max-h-32 overflow-auto whitespace-pre-wrap break-all">
                {JSON.stringify(e.payload, null, 2)}
              </pre>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function labelForKind(kind: string): string {
  const map: Record<string, string> = {
    'business.created': 'Negocio creado',
    'business.updated': 'Info actualizada',
    'business.meta_connected': 'Cuentas Meta conectadas',
    'business.meta_disconnected': 'Cuentas Meta desconectadas',
    'business.config_updated': 'Config de contenido actualizada',
    'business.deactivated': 'Desactivado',
    'business.activated': 'Activado',
    'business.deleted': 'Eliminado (soft)',
    'business.restored': 'Restaurado',
  };
  return map[kind] || kind;
}

// ── UI primitives ─────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] text-white/50 uppercase tracking-wider">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1 text-sm text-white/70 cursor-pointer">
      <span>{label}</span>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 border-b border-white/5 last:border-0 text-xs">
      <span className="text-white/50">{label}</span>
      <span className="text-white/80">{value}</span>
    </div>
  );
}

export default BusinessAdmin;
